import React, { useState, useEffect, useCallback } from "react";
import { Search } from "lucide-react";
import { X, AlertTriangle } from 'lucide-react';
import { authFetch } from "../../lib/api";
import Icons from "../../components/icons";
import AddAssetModal from "./components/AddAssetModal";
import EditAssetDrawer from "./components/EditAssetDrawer";
import ReturnAssetDrawer from "./components/ReturnAssetDrawer";
import AssignAssetModal from "./components/AssignAssetModal";

const MONTH = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return `${dt.getDate()} ${MONTH[dt.getMonth()]}, ${dt.getFullYear()}`;
};

const AssetManagement = () => {
  const [view, setView] = useState("assign"); // "assign" | "assets"
  const [search, setSearch] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalResults: 0,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [showAssign, setShowAssign] = useState(false);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showEditInventory, setShowEditInventory] = useState(false);
  const [showReturn, setShowReturn] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetError, setAssetError] = useState("");

  // ── Delete inventory item: confirm dialog, or a blocked-delete dialog
  //    showing the backend's own message (e.g. "assigned, return it first") ──
  const [deleteTarget, setDeleteTarget] = useState(null); // inventory item pending delete confirmation
  const [deleteBlockedMessage, setDeleteBlockedMessage] = useState("");
  const [deleting, setDeleting] = useState(false);

  // ── Fetch assignments ──────────────────────────────────────────────────────
  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 5 });
      if (search) params.set("search", search);
      const res = await authFetch(`/api/assets/assignments?${params}`);
      const json = await res.json();
      if (json?.data) {
        setAssignments(json.data);
        setPagination(json.pagination || {});
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  // ── Fetch inventory ────────────────────────────────────────────────────────
  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 5 });
      if (search) params.set("search", search);
      const res = await authFetch(`/api/assets/inventory?${params}`);
      const json = await res.json();
      if (json?.data) {
        setInventory(json.data);
        setPagination(json.pagination || {});
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    if (view === "assign") fetchAssignments();
    else fetchInventory();
  }, [view, fetchAssignments, fetchInventory]);

  // Reset page when switching views or searching
  useEffect(() => {
    setPage(1);
  }, [view, search]);

  // ── Add asset to inventory ─────────────────────────────────────────────────
  const handleAddAsset = async (data) => {
    setAssetError("");
    try {
      const res = await authFetch("/api/assets/inventory", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category: data.assetType,
          brand: data.brand,
          modelName: data.modelName,
          serialNumber: data.serialNumber,
          warrantyExpiry: data.warrantyExpiry,
          assignTo: data.assignedUserId,
        }),
      });
      const json = await res.json();
      if (res.ok) {
        setShowAddAsset(false);
        setAssetError("");
        fetchInventory();
        return true;
      }
      setAssetError(json?.message || "Unable to add asset. Please try again.");
      return false;
    } catch (error) {
      setAssetError(
        "Unable to add asset. Please check your session or backend connection.",
      );
      return false;
    }
  };

  // ── Edit assignment ────────────────────────────────────────────────────────
  const handleSaveEdit = async (data) => {
    if (!selectedAsset?._id) return;
    setAssetError("");
    try {
      const payload = {
        category: data?.category || data?.assetType || selectedAsset?.category,
        assignDate: data?.assignDate || selectedAsset?.assignDate,
        returnDate: data?.returnDate || null,
        status: (data?.status || selectedAsset?.status || "ACTIVE")
          .toString()
          .toUpperCase(),
      };

      if (payload.status === "RETURN") payload.status = "RETURNED";

      const res = await authFetch(`/api/assets/${selectedAsset._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (res.ok) {
        setShowEdit(false);
        setSelectedAsset(null);
        setAssetError("");
        await fetchAssignments();
        return true;
      }
      setAssetError(
        json?.message || "Unable to update asset. Please try again.",
      );
      return false;
    } catch (error) {
      setAssetError(
        "Unable to update asset. Please check your session or backend connection.",
      );
      return false;
    }
  };

  const handleEditAsset = async (data) => {
    if (!selectedAsset?._id) return false;
    setAssetError("");
    try {
      const res = await authFetch(
        `/api/assets/inventory/${selectedAsset._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            category: data.category || data.assetType,
            brand: data.brand,
            modelName: data.modelName,
            serialNumber: data.serialNumber,
            warrantyExpiry: data.warrantyExpiry,
          }),
        },
      );
      const json = await res.json();
      if (res.ok) {
        setShowEditInventory(false);
        setSelectedAsset(null);
        setAssetError("");
        fetchInventory();
        return true;
      }
      setAssetError(
        json?.message || "Unable to update asset. Please try again.",
      );
      return false;
    } catch (error) {
      setAssetError(
        "Unable to update asset. Please check your session or backend connection.",
      );
      return false;
    }
  };

  // ── Delete inventory item ──────────────────────────────────────────────────
  const handleConfirmDeleteInventory = async () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    try {
      const res = await authFetch(`/api/assets/inventory/${deleteTarget._id}`, {
        method: "DELETE",
      });
      const json = await res.json();
      if (res.ok) {
        setDeleteTarget(null);
        fetchInventory();
        return;
      }
      // Backend blocks deletion (e.g. asset is ASSIGNED) — surface its
      // exact message in the "Cannot Delete Asset" dialog.
      setDeleteTarget(null);
      setDeleteBlockedMessage(
        json?.message || "Unable to delete this asset. Please try again.",
      );
    } catch (error) {
      setDeleteTarget(null);
      setDeleteBlockedMessage(
        "Unable to delete this asset. Please check your session or backend connection.",
      );
    } finally {
      setDeleting(false);
    }
  };

  // ── Return asset ───────────────────────────────────────────────────────────
  const handleReturn = async (data) => {
    if (!selectedAsset?._id) return false;
    setAssetError("");
    try {
      const status =
        (data?.status || "RETURNED").toString().toUpperCase() === "ACTIVE"
          ? "ACTIVE"
          : "RETURNED";
      const payload = {
        status,
        returnDate:
          status === "ACTIVE"
            ? null
            : data?.returnDate || selectedAsset?.returnDate || null,
      };

      const res = await authFetch(`/api/assets/${selectedAsset._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (res.ok) {
        setShowReturn(false);
        setSelectedAsset(null);
        setAssetError("");
        await fetchAssignments();
        return true;
      }
      setAssetError(
        json?.message || "Unable to mark asset as returned. Please try again.",
      );
      return false;
    } catch (error) {
      setAssetError(
        "Unable to mark asset as returned. Please check your session or backend connection.",
      );
      return false;
    }
  };

// ── Pagination pages ───────────────────────────────────────────────────────
  const totalPages = pagination.totalPages || 1;
  function Pagination({ current, total, onChange }) {
    if (total <= 1) return null;
    return (
      <div className="flex gap-3 items-center">
        <button
          className="w-10 h-10 border border-[#ECEFF5] rounded-lg text-[0px] bg-[#F5F5F5] flex items-center justify-center disabled:opacity-60 before:block before:w-3 before:h-3 before:border-l-2 before:border-b-2 before:border-gray-700 before:rotate-45"
          disabled={current === 1}
          onClick={() => onChange(current - 1)}
        >
          ‹
        </button>
        {Array.from({ length: total }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            className={`w-8 h-8 rounded-lg text-xs ${p === current ? "bg-[#4C5CE7] text-white" : "text-black"}`}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        ))}
        <button
          className="w-10 h-10 border border-[#ECEFF5] rounded-lg text-[0px] bg-[#F5F5F5] flex items-center justify-center disabled:opacity-60 before:block before:w-3 before:h-3 before:border-r-2 before:border-t-2 before:border-gray-700 before:rotate-45"
          disabled={current === total}
          onClick={() => onChange(current + 1)}
        >
          ›
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="mt-5 bg-white rounded-2xl p-5">
        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div className="relative w-full sm:w-[250px]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="w-full h-10 border rounded-lg pl-9 text-sm outline-none"
            />
          </div>
          <div className="flex gap-3">
            {view === "assign" ? (
              <>
                <button
                  onClick={() => setView("assets")}
                  className="w-[163px] h-[40px] border border-[#4C5CE7] rounded-lg text-[#4C5CE7] text-sm"
                >
                  Assets
                </button>
                <button
                  onClick={() => setShowAssign(true)}
                  className="w-[163px] h-[40px] bg-[#4253C7] rounded-lg text-white text-sm"
                >
                  + Assign Asset
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setView("assign")}
                  className="w-[163px] h-[40px] border border-[#4C5CE7] rounded-lg text-[#4C5CE7] text-sm"
                >
                  Back
                </button>
                <button
                  onClick={() => setShowAddAsset(true)}
                  className="w-[163px] h-[40px] bg-[#4253C7] rounded-lg text-white text-sm"
                >
                  + Add Asset
                </button>
              </>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          {/* ── ASSIGN VIEW ── */}
          {view === "assign" ? (
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-400 border-b text-left">
                  {[
                    "Resource",
                    "Emp ID",
                    "Asset Type",
                    "Asset",
                    "Assigned Date",
                    "Return Date",
                    "Status",
                    "Action",
                  ].map((h) => (
                    <th key={h} className="pb-4 pr-4 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 text-center text-sm text-gray-400"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : assignments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 text-center text-sm text-gray-400"
                    >
                      No assignments found
                    </td>
                  </tr>
                ) : (
                  assignments.map((item) => (
                    <tr key={item._id} className="border-b text-sm">
                      {/* Resource */}
                      <td className="py-4 pr-4">
                        {item.resource?.name ? (
                          <div className="flex items-center gap-2">
                            {item.resource.profilePicture ? (
                              <img
                                src={item.resource.profilePicture}
                                alt=""
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                                {item.resource.name[0]}
                              </div>
                            )}
                            <div>
                              <p className="text-xs font-medium">
                                {item.resource.name}
                              </p>
                              <p className="text-[10px] text-gray-400">
                                {item.resource.email}
                              </p>
                            </div>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>

                      {/* Emp ID */}
                      <td className="text-xs pr-4">{item.empId || "—"}</td>
                      {/* Asset Type */}
                      <td className="pr-4">
                        <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-500 text-[10px]">
                          {item.category}
                        </span>
                      </td>
                      {/* Asset */}
                    {/* Asset */}
<td className="pr-4">
  {item.category === "ID Card" ? (
    <>
      <p className="text-xs">Employee ID</p>
      <p className="text-[10px] text-gray-400">
        {`${item.empId || ""}-${(item.resource?.name || "").replace(/\s+/g, "")}`}
      </p>
    </>
  ) : (
    <>
      <p className="text-xs">
        {item.modelName || item.name || "—"}
      </p>
      <p className="text-[10px] text-gray-400">
        {item.serialNumber || ""}
      </p>
    </>
  )}
</td>
                      {/* Assigned Date */}
                      <td className="text-xs pr-4">
                        {fmtDate(item.assignDate)}
                      </td>
                      {/* Return Date */}
                      <td className="text-xs pr-4">
                        {item.returnDate ? fmtDate(item.returnDate) : "—"}
                      </td>
                      {/* Status */}
                      <td className="pr-4">
                        <span
                          className={`px-3 py-1 rounded-full text-[10px] ${item.status === "ACTIVE" ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      {/* Actions */}
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedAsset(item);
                              setShowEdit(true);
                            }}
                            className="w-6 h-6 bg-[#EEF0FF] rounded flex items-center justify-center text-[#4C5CE7]"
                          >
                            <Icons.Assetpen />
                          </button>
                          {item.status === "ACTIVE" && (
                            <button
                              onClick={() => {
                                setSelectedAsset(item);
                                setShowReturn(true);
                              }}
                              className="w-6 h-6 bg-[#EEF0FF] rounded flex items-center justify-center text-[#4C5CE7]"
                            >
                              <Icons.Return />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            /* ── INVENTORY VIEW ── */
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-400 border-b text-left">
                  {[
                    "Asset Type",
                    "Brand",
                    "Model Name",
                    "Serial Number",
                    "Warranty Expiry",
                    "Status",
                    "Action",
                  ].map((h) => (
                    <th key={h} className="pb-4 pr-4 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-10 text-center text-sm text-gray-400"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : inventory.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-10 text-center text-sm text-gray-400"
                    >
                      No assets in inventory
                    </td>
                  </tr>
                ) : (
                  inventory.map((item) => (
                    <tr key={item._id} className="border-b text-sm">
                      <td className="py-4 pr-4">
                        <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-500 text-[10px]">
                          {item.category}
                        </span>
                      </td>
                      <td className="text-xs pr-4">{item.brand || "—"}</td>
                      <td className="text-xs pr-4">{item.modelName || "—"}</td>
                      <td className="text-xs pr-4">
                        {item.serialNumber || "—"}
                      </td>
                      <td className="text-xs pr-4">
                        {item.warrantyExpiry
                          ? fmtDate(item.warrantyExpiry)
                          : "—"}
                      </td>
                      <td className="pr-4">
                        <span
                          className={`px-3 py-1 rounded-full text-[10px] ${item.status === "AVAILABLE" ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setDeleteTarget(item)}
                            title="Delete asset"
                          >
                            <Icons.AssetDelete />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedAsset(item);
                              setShowEditInventory(true);
                            }}
                          >
                            <Icons.Assetpen />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer / Pagination */}
        {pagination.totalResults > 0 && (
          <div className="bg-[#F5F5F5] border-t border-[#F5F5F5] h-[67px] px-6 -mx-5 -mb-5 mt-10 flex justify-between items-center gap-4 text-xs text-gray-700">
            <span>
              {pagination.showing
                ? `Showing ${pagination.showing.from} to ${pagination.showing.to} of ${pagination.showing.of} Results`
                : `Showing ${assignments.length || inventory.length} Results`}
            </span>
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </div>
        )}
      </div>

      {/* Modals */}
      {showAssign && (
        <AssignAssetModal
          onClose={() => setShowAssign(false)}
          onSuccess={() => {
            fetchAssignments();
          }}
        />
      )}
      {showAddAsset && (
        <AddAssetModal
          onClose={() => {
            setShowAddAsset(false);
            setAssetError("");
          }}
          onSubmit={handleAddAsset}
          errorMessage={assetError}
        />
      )}
      {showEditInventory && selectedAsset && (
        <AddAssetModal
          onClose={() => {
            setShowEditInventory(false);
            setSelectedAsset(null);
            setAssetError("");
          }}
          onSubmit={handleEditAsset}
          initialData={selectedAsset}
          mode="edit"
          errorMessage={assetError}
        />
      )}
      {showEdit && selectedAsset && (
        <EditAssetDrawer
          asset={selectedAsset}
          onClose={() => {
            setShowEdit(false);
            setSelectedAsset(null);
            setAssetError("");
          }}
          onSave={handleSaveEdit}
          errorMessage={assetError}
        />
      )}
      {showReturn && selectedAsset && (
        <ReturnAssetDrawer
          asset={selectedAsset}
          onClose={() => {
            setShowReturn(false);
            setSelectedAsset(null);
            setAssetError("");
          }}
          onConfirm={handleReturn}
          errorMessage={assetError}
        />
      )}

      {/* DELETE ASSET — confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/30 z-[2000] flex items-center justify-center p-4">
          <div className="w-full max-w-[420px] bg-white rounded-xl shadow-2xl p-6 relative text-center">
            <button
              onClick={() => setDeleteTarget(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="text-red-500" size={22} />
            </div>
            <h2 className="text-base font-bold text-black mb-2">
              Delete Asset?
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to permanently delete this{" "}
              {(deleteTarget.status || "").toLowerCase()} asset?
              <br />
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 h-10 border border-red-500 text-red-500 rounded-lg text-sm font-medium hover:bg-red-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteInventory}
                disabled={deleting}
                className="flex-1 h-10 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE ASSET — blocked (backend message, e.g. asset is assigned) */}
      {deleteBlockedMessage && (
        <div className="fixed inset-0 bg-black/30 z-[2000] flex items-center justify-center p-4">
          <div className="w-full max-w-[420px] bg-white rounded-xl shadow-2xl p-6 relative text-center">
            <button
              onClick={() => setDeleteBlockedMessage("")}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="text-red-500" size={22} />
            </div>
            <h2 className="text-base font-bold text-black mb-2">
              Cannot Delete Asset
            </h2>
            <p className="text-sm text-gray-500 mb-6">{deleteBlockedMessage}</p>
            <button
              onClick={() => setDeleteBlockedMessage("")}
              className="w-full h-10 bg-[#4253C7] text-white rounded-lg text-sm font-semibold hover:bg-[#3644b0]"
            >
              Okay
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AssetManagement;
